import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Copy,
  ExternalLink,
  GraduationCap,
  HeartHandshake,
  Layers,
  Sparkles,
  X,
} from "lucide-react";

const SUPPORT_EMAIL = "admin@acaera.ietlucknow.ac.in";
const INNOVGEIST_URL = "https://www.innovgeist.com";

const SamlFailure = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [copiedField, setCopiedField] = useState(null);
  const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const error = params.get("error") || "Authentication failed";
  const message =
    params.get("message") ||
    "We couldn’t sign you in. Please try again, or contact your administrator.";
  const code = params.get("code") || "SAML_AUTH_FAILED";
  const email = params.get("email") ? decodeURIComponent(params.get("email")) : "";

  const primaryTitle =
    code === "USER_NOT_ONBOARDED" ? "Access Pending Approval" : "Sign-in Failed";

  useEffect(() => {
    if (code === "USER_NOT_ONBOARDED") {
      setIsAccessModalOpen(true);
    }
  }, [code]);

  const mailto = useMemo(() => {
    const subject = encodeURIComponent(`[Acaera] SAML Login Issue (${code})`);
    const body = encodeURIComponent(
      `Hi Admin,\n\nI tried signing in to Acaera but received an error.\n\n` +
      `Email: ${email || "(not provided)"}\n` +
      `Code: ${code}\n` +
      `Error: ${error}\n` +
      `Message: ${message}\n\n` +
      `Please help me get access.\n`
    );
    return `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
  }, [code, email, error, message]);

  const copyToClipboard = async (value, fieldName) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(fieldName);
      window.setTimeout(() => setCopiedField(null), 1200);
    } catch {
      // Ignore (some browsers block clipboard without user permissions).
    }
  };

  const aboutCards = [
    {
      icon: Sparkles,
      title: "NEP-first, AI-integrated",
      body:
        "An AI-integrated Academic Operating System (AOS) designed to help institutions implement NEP 2020 in practice.",
    },
    {
      icon: Layers,
      title: "End-to-end academic lifecycle",
      body:
        "Digitises enrollment, assessments, grading, credit validation, and outcome tracking—beyond just content delivery.",
    },
    {
      icon: GraduationCap,
      title: "Built for Indian institutions",
      body:
        "Supports credit-based modular learning, OBE, personalised pathways, and MOOC/interdisciplinary credit integration.",
    },
    {
      icon: HeartHandshake,
      title: "A product by Innovgeist",
      body:
        "Acaera is developed by Innovgeist to help institutions modernise academic operations and implement NEP-aligned workflows.",
    },
  ];

  const pilotBlocks = [
    {
      title: "Purpose of the pilot",
      points: [
        "Support real academic workflows",
        "Enable credit-based assessment and reporting",
        "Improve visibility and efficiency for faculty and administrators",
        "Test scalability for multi-department adoption",
      ],
    },
    {
      title: "Pilot scope",
      points: [
        "Student onboarding and enrollment workflows",
        "Digital assessments and grading",
        "Automated credit summaries",
        "Feedback-driven system refinement",
      ],
    },
    {
      title: "Outcome focus",
      points: [
        "Institutional readiness for NEP compliance",
        "Practical deployment of an Academic Operating System",
        "Future expansion to additional institutions and universities",
      ],
    },
  ];

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-white text-gray-900 dark:bg-gray-900 dark:text-white">
      {/* background */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 overflow-hidden"
      >
        <div className="absolute -top-40 left-1/2 h-[520px] w-[820px] -translate-x-1/2 rounded-full bg-gradient-to-b from-accent2/70 to-transparent blur-3xl dark:from-accent1/15" />
        <div className="absolute inset-x-0 top-0 h-64 bg-gradient-to-b from-white via-white/80 to-transparent dark:from-gray-900 dark:via-gray-900/70" />
        <div className="absolute inset-0 opacity-[0.04] dark:opacity-[0.06]">
          <div className="h-full w-full [background-image:linear-gradient(to_right,#000_1px,transparent_1px),linear-gradient(to_bottom,#000_1px,transparent_1px)] [background-size:48px_48px] dark:[background-image:linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)]" />
        </div>
      </div>

      {/* Access pending modal (auto-opens for USER_NOT_ONBOARDED) */}
      {code === "USER_NOT_ONBOARDED" && isAccessModalOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-label="Access pending approval"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
            onClick={() => setIsAccessModalOpen(false)}
            aria-label="Close modal"
          />

          <div className="relative w-full max-w-2xl rounded-3xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl overflow-hidden">
            <div className="p-6 sm:p-7">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-xs font-semibold tracking-wide text-gray-500 dark:text-gray-300">
                    SIGN-IN STATUS
                  </div>
                  <div className="mt-1 text-2xl font-bold">{primaryTitle}</div>
                  <div className="mt-1 text-sm text-gray-600 dark:text-gray-200">
                    {error}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsAccessModalOpen(false)}
                  className="inline-flex items-center justify-center h-10 w-10 rounded-2xl border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <p className="mt-4 text-sm sm:text-base text-gray-700 dark:text-gray-200">
                {message}
              </p>

              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="rounded-2xl bg-gray-50 dark:bg-gray-900/40 border border-gray-200/70 dark:border-gray-700/60 p-4">
                  <div className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                    CODE
                  </div>
                  <div className="mt-1 font-mono text-xs sm:text-sm text-gray-900 dark:text-gray-100 break-all">
                    {code}
                  </div>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(code, "code_modal")}
                    className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                  >
                    {copiedField === "code_modal" ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                    {copiedField === "code_modal" ? "Copied" : "Copy"}
                  </button>
                </div>

                <div className="rounded-2xl bg-gray-50 dark:bg-gray-900/40 border border-gray-200/70 dark:border-gray-700/60 p-4">
                  <div className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                    EMAIL
                  </div>
                  <div className="mt-1 font-mono text-xs sm:text-sm text-gray-900 dark:text-gray-100 break-all">
                    {email || "(not provided)"}
                  </div>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(email, "email_modal")}
                    disabled={!email}
                    className={`mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-sm transition ${email
                        ? "border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                        : "border-gray-200/60 dark:border-gray-700/50 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                      }`}
                  >
                    {copiedField === "email_modal" ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                    {copiedField === "email_modal" ? "Copied" : "Copy"}
                  </button>
                </div>
              </div>

              <div className="mt-5 rounded-2xl border border-accent1/20 bg-accent2/40 dark:bg-accent1/10 p-4">
                <div className="text-sm font-semibold">How to get onboarded</div>
                <ul className="mt-2 text-sm text-gray-700 dark:text-gray-200 space-y-1.5">
                  <li>Request onboarding from your department/admin for the pilot rollout.</li>
                  <li>Share your institute email and program/section.</li>
                  <li>After approval, return and sign in again.</li>
                </ul>
              </div>

              {/* <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <a
                  href={mailto}
                  className="w-full inline-flex items-center justify-center gap-2 py-3 px-4 rounded-2xl shadow-sm text-sm font-semibold text-white bg-accent1 hover:bg-accent1/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent1 transition"
                >
                  Contact Administrator
                  <ExternalLink className="h-4 w-4" />
                </a>
                <button
                  type="button"
                  onClick={() => {
                    setIsAccessModalOpen(false);
                    navigate("/login", { replace: true });
                  }}
                  className="w-full inline-flex items-center justify-center gap-2 py-3 px-4 rounded-2xl border border-gray-300 dark:border-gray-600 text-sm font-semibold text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                >
                  Back to Login
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div> */}
            </div>
          </div>
        </div>
      ) : null}

      <div className="relative w-full px-4 sm:px-6 lg:px-10 2xl:px-16 py-10">
        {/* header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center justify-center sm:justify-start gap-4 w-full sm:w-auto flex-wrap">
            <div className="flex items-center gap-2">
              <img
                src="/Logo_new_transparent.png"
                alt="Acaera Logo"
                className="h-10 sm:h-12 w-auto object-contain dark:invert dark:hue-rotate-180"
              />
              <span className="font-bold text-[#1a365d] dark:text-white text-3xl sm:text-4xl tracking-wide">
                Acaera
              </span>
            </div>
            <div
              className="hidden sm:block h-8 sm:h-10 w-px bg-gray-800 dark:bg-gray-700"
              aria-hidden="true"
            />
            <img
              src="/ietlogo.jpg"
              alt="IET Lucknow"
              className="h-9 w-9 sm:h-12 sm:w-12 object-contain mix-blend-multiply dark:mix-blend-normal dark:bg-white dark:rounded-full"
            />
          </div>

          <div className="flex  sm:items-end gap-2 ">


            {/* <div className="text-xs text-gray-600 dark:text-gray-300 sm:text-right">
              <div className="font-semibold">Acaera × IET Lucknow (Pilot)</div>
              <div className="flex flex-wrap sm:justify-end items-center gap-x-2 gap-y-1">
                <span>Public information page</span>
                <span aria-hidden="true">•</span>
                <a
                  href={INNOVGEIST_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-accent1 hover:underline"
                >
                  Developed by Innovgeist
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
            </div> */}
            <div className="mt-6 flex flex-col sm:flex-row gap-3 w-full">
              <a
                href={mailto}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 py-3.5 sm:py-3 px-4 rounded-2xl shadow-sm text-sm font-semibold text-white bg-accent1 hover:bg-accent1/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent1 transition text-center"
              >
                Contact Administrator
                <ExternalLink className="h-4 w-4 shrink-0" />
              </a>
              <button
                type="button"
                onClick={() => {
                  setIsAccessModalOpen(false);
                  navigate("/login", { replace: true });
                }}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 py-3.5 sm:py-3 px-4 rounded-2xl border border-gray-300 dark:border-gray-600 text-sm font-semibold text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 transition text-center"
              >
                Login

              </button>
            </div>
          </div>
        </div>

        {/* hero */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7 flex flex-col gap-4 items-start">

            <div className="inline-flex items-center gap-2 rounded-full border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-800/60 px-3 py-1.5 text-xs text-gray-700 dark:text-gray-200">
              <HeartHandshake className="h-4 w-4 text-accent1" />
              NEP 2020-ready academic infrastructure — built with institutions, not in isolation
            </div>

            <h1 className="mt-4 text-4xl sm:text-5xl font-bold tracking-tight">
              Acaera
            </h1>
            <p className="mt-3 text-base sm:text-lg text-gray-700 dark:text-gray-200 max-w-3xl">
              An <span className="font-semibold">AI-integrated Academic Operating System (AOS)</span> designed to help higher-education institutions in India{" "}
              <span className="font-semibold">implement NEP 2020 in practice</span>.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-600 dark:text-gray-300">
              <span className="font-semibold text-gray-900 dark:text-white">Definition:</span>
              <span>Next-generation LMS that digitises the entire academic lifecycle.</span>
              <span aria-hidden="true">•</span>
              <a
                href={INNOVGEIST_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-accent1 hover:underline font-semibold"
              >
                A product by Innovgeist
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>

            <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {aboutCards.map((c) => {
                const Icon = c.icon;
                return (
                  <div
                    key={c.title}
                    className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-800/60 p-5"
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 h-10 w-10 rounded-2xl bg-accent2/70 dark:bg-accent1/15 flex items-center justify-center">
                        <Icon className="h-5 w-5 text-accent1" />
                      </div>
                      <div>
                        <div className="text-sm font-semibold">{c.title}</div>
                        <p className="mt-1.5 text-sm text-gray-700 dark:text-gray-200">
                          {c.body}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* right column: single image */}
          <div className="lg:col-span-5">
            <div className="rounded-3xl border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-800/60 overflow-hidden">
              <img
                src="https://epe.brightspotcdn.com/dims4/default/9c36d5d/2147483647/strip/true/crop/2956x2006+44+0/resize/840x570!/quality/90/?url=https%3A%2F%2Fepe-brightspot.s3.us-east-1.amazonaws.com%2Ffe%2F76%2Fc5f43ff4413aa22153fdc121a1f4%2Fai-robot-learning-082023-1497617002-01.jpg"
                alt="Acaera"
                className="h-64 sm:h-80 lg:h-[560px] w-full object-cover"
              />
            </div>
          </div>
        </div>

        {/* ABOUT */}
        <section className="mt-14">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-2xl bg-accent2/70 dark:bg-accent1/15 flex items-center justify-center">
              <Sparkles className="h-4.5 w-4.5 text-accent1" />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight">
              About Acaera
            </h2>
          </div>

          <div className="mt-4 rounded-3xl border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-800/60 p-6">
            <p className="text-sm sm:text-base text-gray-700 dark:text-gray-200 leading-relaxed text-center sm:text-left">
              <span className="font-semibold">Acaera</span> is an{" "}
              <span className="font-semibold">
                AI-integrated Academic Operating System (AOS)
              </span>{" "}
              designed to help higher-education institutions in India{" "}
              <span className="font-semibold">
                implement NEP 2020 in practice
              </span>
              .
            </p>
            <p className="mt-3 text-sm sm:text-base text-gray-700 dark:text-gray-200 leading-relaxed">
              It functions as a next-generation Learning Management System (LMS)
              that goes beyond content delivery by digitising the{" "}
              <span className="font-semibold">entire academic lifecycle</span>
              —from enrollment and assessments to grading, credit validation, and
              outcome tracking.
            </p>
            <p className="mt-3 text-sm sm:text-base text-gray-700 dark:text-gray-200 leading-relaxed">
              Unlike traditional LMS platforms, Acaera is{" "}
              <span className="font-semibold">NEP-first by design</span>,
              lightweight to deploy, and scalable across departments, campuses,
              and institutions.
            </p>
          </div>
        </section>

        {/* PILOT */}
        <section className="mt-10">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-2xl bg-accent2/70 dark:bg-accent1/15 flex items-center justify-center">
              <HeartHandshake className="h-4.5 w-4.5 text-accent1" />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight">
              Acaera × IET Lucknow — Pilot partnership
            </h2>
          </div>

          <div className="mt-4 rounded-3xl border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-800/60 p-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
              <div className="lg:col-span-5">
                <div className="h-full lg:sticky lg:top-6 rounded-3xl border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-900/30 overflow-hidden">
                  <img
                    src="/image1.jpg"
                    alt="IET Lucknow campus"
                    className="h-full w-full object-cover"
                  />
                </div>
              </div>

              <div className="lg:col-span-7">
                <p className="text-sm sm:text-base text-gray-700 dark:text-gray-200 leading-relaxed">
                  Acaera is currently being{" "}
                  <span className="font-semibold">
                    piloted in partnership with IET Lucknow
                  </span>
                  , a leading government engineering institution. The pilot validates real academic workflows and helps refine Acaera with feedback from faculty, administrators, and students.
                </p>

                <div className="mt-5 space-y-4">
                  {pilotBlocks.map((b) => (
                    <div
                      key={b.title}
                      className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-900/30 p-5"
                    >
                      <div className="text-sm font-semibold text-gray-900 dark:text-white">
                        {b.title}
                      </div>
                      <ul className="mt-3 space-y-2 text-sm text-gray-700 dark:text-gray-200">
                        {b.points.map((p) => (
                          <li key={p} className="flex items-start gap-2">
                            <span className="mt-2 inline-block h-1.5 w-1.5 rounded-full bg-accent1" />
                            <span>{p}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* VISION */}
        <section className="mt-10">
          <div className="rounded-3xl border border-gray-200 dark:border-gray-700 bg-gradient-to-br from-accent2/60 to-white/60 dark:from-accent1/15 dark:to-gray-900/40 p-6">
            <div className="text-sm font-semibold text-gray-900 dark:text-white text-center sm:text-left">
              Vision going forward
            </div>
            <p className="mt-2 text-sm sm:text-base text-gray-700 dark:text-gray-200 leading-relaxed text-left sm:text-left">
              Through the Acaera × IET Lucknow pilot, the platform is being refined to support multi-institution onboarding,
              state-level and national-level deployment, large-scale student adoption, and advanced AI-powered academic analytics.
              Acaera aims to become a{" "}
              <span className="font-semibold">
                standard digital backbone
              </span>{" "}
              for institutions transitioning to{" "}
              <span className="font-semibold">
                NEP-aligned, outcome-driven, student-centric
              </span>{" "}
              higher education.
            </p>
          </div>
        </section>

        {/* footer */}
        <footer className="mt-10 pb-2">
          <div className="flex flex-col items-center text-center sm:text-left sm:flex-row sm:items-center sm:justify-between gap-3 text-xs text-gray-500 dark:text-gray-300">
            <div className="max-w-xs sm:max-w-none">
              <span className="font-semibold text-gray-700 dark:text-gray-200">
                Acaera
              </span>{" "}
              — AI-integrated Academic Operating System (AOS)
            </div>
            <a
              href={INNOVGEIST_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center sm:justify-start gap-1 text-accent1 hover:underline break-all sm:break-normal"
            >
              Developed by Innovgeist • {INNOVGEIST_URL.replace("https://", "")}
              <ExternalLink className="h-3.5 w-3.5 shrink-0" />
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default SamlFailure;


